import { Button } from "@/components/ui/button";
import query from "@/Utils/request/query";
import { HttpMethod, Type } from "@/Utils/request/types";
import careConfig from "@careConfig";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import "./queueDisplay.css";

type Call = {
  patient_name?: string | null;
  status?: string;
  id: string;
  number: string;
  station: string;
  event: string;
};
type Display = {
  rotation_seconds?: number;
  hold_seconds?: number;
  facility: string;
  language: string;
  updated: string;
  queues: {
    id: string;
    name: string;
    practitioner_name?: string | null;
    calling: Call[];
    waiting: Call[];
    missed?: Call[];
  }[];
};
const endpoint = {
  path: "/api/visitflow/queue-display/{key}/",
  method: HttpMethod.GET,
  noAuth: true,
  TRes: Type<Display>(),
};
export default function QueueDisplay({ displayKey }: { displayKey: string }) {
  const [disconnectedAt, setDisconnectedAt] = useState(0);
  const [networkOnline, setNetworkOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => {
      setNetworkOnline(navigator.onLine);
      if (!navigator.onLine) setDisconnectedAt(Date.now());
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  const [wakeRequested, setWakeRequested] = useState(false);
  const [wakeStatus, setWakeStatus] = useState("");
  const [remembered] = useState(
    () => localStorage.getItem(`tv-audio:${displayKey}`) === "on",
  );
  const [sound, setSound] = useState(false);
  const [clock, setClock] = useState(new Date());
  const spoken = useRef(new Set<string>());
  const audio = useRef<AudioContext | null>(null);
  const seen = useRef(new Set<string>());
  const initialized = useRef(false);
  const activeCall = useRef(false);
  const pending = useRef<{ queue: string; call: Call }[]>([]);
  const deadline = useRef(0);
  const [active, setActive] = useState<{ queue: string; call?: Call }>();
  const [waitingPage, setWaitingPage] = useState(0);
  const [connected, setConnected] = useState(false);
  const { data, isError, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["tv-queue", displayKey],
    queryFn: query(endpoint, { pathParams: { key: displayKey } }),
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const stale =
    !networkOnline ||
    (disconnectedAt > 0 && dataUpdatedAt <= disconnectedAt) ||
    isError ||
    (Boolean(data) && clock.getTime() - dataUpdatedAt > 15000);
  const staleRef = useRef(stale);
  staleRef.current = stale;
  useEffect(() => {
    if (!stale) return;
    setActive(undefined);
    pending.current = [];
    initialized.current = false;
    activeCall.current = false;
    deadline.current = 0;
    if ("speechSynthesis" in window) speechSynthesis.cancel();
  }, [stale]);
  useEffect(() => {
    if (!wakeRequested) return;
    if (!("wakeLock" in navigator)) {
      setWakeStatus("Keep-awake unsupported on this device");
      return;
    }
    let lock: WakeLockSentinel | undefined;
    let disposed = false;
    const acquire = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const next = await navigator.wakeLock.request("screen");
        if (disposed) {
          void next.release();
          return;
        }
        lock = next;
        setWakeStatus("Screen awake");
        next.addEventListener("release", () =>
          setWakeStatus("Screen wake lock released"),
        );
      } catch {
        setWakeStatus("Keep-awake unavailable; check device power settings");
      }
    };
    void acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", acquire);
      void lock?.release();
    };
  }, [wakeRequested]);
  const ml = data?.language === "ml";
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!data) return;
    for (const q of data.queues)
      for (const call of q.calling) {
        if (!seen.current.has(call.event) && initialized.current) {
          pending.current.push({ queue: q.id, call });
          if (!activeCall.current) deadline.current = 0;
        }
        seen.current.add(call.event);
      }
    initialized.current = true;
  }, [data]);
  useEffect(() => {
    if (!data || stale || clock.getTime() < deadline.current) return;
    if (
      activeCall.current &&
      sound &&
      "speechSynthesis" in window &&
      speechSynthesis.speaking
    )
      return;
    const next = pending.current.shift();
    if (next) {
      activeCall.current = true;
      setActive(next);
      setWaitingPage(0);
      deadline.current = clock.getTime() + (data.hold_seconds ?? 15) * 1000;
    } else {
      activeCall.current = false;
      const index = data.queues.findIndex((q) => q.id === active?.queue);
      const current = data.queues[index];
      if (
        !active?.call &&
        current &&
        (waitingPage + 1) * 5 < Math.max(0, current.waiting.length - 5)
      )
        setWaitingPage(waitingPage + 1);
      else {
        setActive({ queue: data.queues[(index + 1) % data.queues.length]?.id });
        setWaitingPage(0);
      }
      deadline.current = clock.getTime() + (data.rotation_seconds ?? 10) * 1000;
    }
  }, [clock, data, isError, active, waitingPage, sound, stale]);
  useEffect(() => {
    const call = active?.call;
    if (
      !call ||
      stale ||
      !sound ||
      !("speechSynthesis" in window) ||
      spoken.current.has(call.event)
    )
      return;
    spoken.current.add(call.event);
    if (audio.current?.state === "running") {
      const oscillator = audio.current.createOscillator();
      const gain = audio.current.createGain();
      oscillator.connect(gain);
      gain.connect(audio.current.destination);
      gain.gain.setValueAtTime(0.08, audio.current.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        audio.current.currentTime + 0.35,
      );
      oscillator.frequency.value = 880;
      oscillator.start();
      oscillator.stop(audio.current.currentTime + 0.35);
    }
    const utterance = new SpeechSynthesisUtterance(
      ml
        ? `ടോക്കൺ ${call.number}, ${call.station} ലേക്ക് വരിക`
        : `Token ${call.number}, please proceed to ${call.station}`,
    );
    utterance.lang = ml ? "ml-IN" : "en-IN";
    const voice = speechSynthesis
      .getVoices()
      .find((v) => v.lang.startsWith(ml ? "ml" : "en"));
    if (voice) utterance.voice = voice;
    speechSynthesis.speak(utterance);
  }, [active, sound, ml, stale]);
  useEffect(() => {
    let socket: WebSocket;
    let retry: ReturnType<typeof setTimeout>;
    let stopped = false;
    const connect = () => {
      const url = new URL(careConfig.apiUrl || window.location.origin);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      if (url.hostname === "localhost" || url.hostname === "127.0.0.1")
        url.port = "9002";
      url.pathname = `/queue-display/${displayKey}/`;
      socket = new WebSocket(url);
      socket.onopen = () => {
        setConnected(true);
        void refetch();
      };
      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data);
          if (
            !staleRef.current &&
            event.call &&
            !seen.current.has(event.call.event)
          ) {
            seen.current.add(event.call.event);
            pending.current.push(event);
            if (!activeCall.current) deadline.current = 0;
          }
          void refetch();
        } catch {
          /* Ignore malformed messages; polling remains active. */
        }
      };
      socket.onclose = () => {
        setConnected(false);
        if (!stopped) retry = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      socket?.close();
    };
  }, [displayKey, refetch]);
  useEffect(
    () => () => {
      if ("speechSynthesis" in window) speechSynthesis.cancel();
    },
    [],
  );
  return (
    <main className="tv-display min-h-svh bg-slate-100 p-3 text-slate-950">
      <header className="tv-header flex flex-wrap items-center justify-between gap-3 mb-3">
        <img src={careConfig.mainLogo?.dark} alt="MA care" className="h-14" />
        <div>
          <h1 className="text-2xl font-semibold">{data?.facility}</h1>
          <p>
            {clock.toLocaleString(ml ? "ml-IN" : "en-IN", { hour12: true })}
          </p>
        </div>
        <div className="tv-controls flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={!("speechSynthesis" in window)}
            onClick={() => {
              if (!sound && "AudioContext" in window) {
                audio.current ??= new AudioContext();
                void audio.current.resume();
              }
              localStorage.setItem(
                `tv-audio:${displayKey}`,
                sound ? "off" : "on",
              );
              setSound(!sound);
              setWakeRequested(true);
              if (sound && "speechSynthesis" in window)
                speechSynthesis.cancel();
            }}
          >
            {sound ? "Mute announcements" : "Enable announcements"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setWakeRequested(!wakeRequested)}
          >
            {wakeRequested ? "Allow screen sleep" : "Keep screen awake"}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              void document.documentElement
                .requestFullscreen?.()
                .catch(() => {})
            }
          >
            Fullscreen
          </Button>
        </div>
      </header>
      <p className="text-sm text-slate-500 mb-3">
        {connected ? "Live connection" : "Polling fallback"}
      </p>
      {remembered && !sound && (
        <p className="tv-controls text-sm mb-2">
          Sound was previously enabled. Click Enable announcements to resume
          audio.
        </p>
      )}
      {wakeStatus && <p className="tv-controls text-sm">{wakeStatus}</p>}
      {stale && (
        <p role="alert" className="rounded-xl bg-amber-100 p-4 mb-4">
          {ml
            ? "ബന്ധം നഷ്ടപ്പെട്ടു. വീണ്ടും ബന്ധിപ്പിക്കുന്നു…"
            : "Queue updates paused. Reconnecting…"}
        </p>
      )}
      {!data && !isError && <p role="status">Loading queue…</p>}
      {data && !stale && (
        <div className="w-full">
          {data.queues
            .filter(
              (q) =>
                q.id ===
                (data.queues.some((row) => row.id === active?.queue)
                  ? active?.queue
                  : data.queues[0]?.id),
            )
            .map((q) => (
              <section key={q.id} className="tv-station">
                <header className="tv-station-heading">
                  <div className="tv-station-count">
                    {q.waiting.length} {ml ? "കാത്തിരിക്കുന്നു" : "waiting"}
                  </div>
                  <h2>{q.practitioner_name || q.name}</h2>
                  {q.practitioner_name && <p>{q.name}</p>}
                </header>
                <div className="tv-rotation-strip">
                  <span>
                    {activeCall.current
                      ? ml
                        ? "ടോക്കൺ വിളിക്കുന്നു"
                        : "Calling patient"
                      : ml
                        ? "അടുത്ത സ്റ്റേഷൻ"
                        : (waitingPage + 1) * 5 <
                            Math.max(0, q.waiting.length - 5)
                          ? "Next waiting page"
                          : "Next station"}
                    :{" "}
                    {!activeCall.current &&
                      (() => {
                        if (
                          (waitingPage + 1) * 5 <
                          Math.max(0, q.waiting.length - 5)
                        )
                          return String(waitingPage + 2);
                        const next =
                          data.queues[
                            (data.queues.findIndex((row) => row.id === q.id) +
                              1) %
                              data.queues.length
                          ];
                        return next?.practitioner_name || next?.name;
                      })()}
                  </span>
                  <span>
                    {Math.max(
                      0,
                      Math.ceil((deadline.current - clock.getTime()) / 1000),
                    )}
                    s
                  </span>
                </div>
                <div className="tv-board">
                  <section className="tv-now">
                    <h3>{ml ? "ഇപ്പോൾ വിളിക്കുന്നത്" : "Now Calling"}</h3>
                    {q.calling.length ? (
                      (active?.call &&
                      active.queue === q.id &&
                      q.calling.some((c) => c.id === active.call?.id)
                        ? [active.call]
                        : q.calling
                      ).map((c) => (
                        <div
                          key={c.id}
                          className={`tv-call ${active?.call?.event === c.event ? "tv-call-highlight" : ""} ${q.calling.length > 1 ? "tv-call-multiple" : ""}`}
                        >
                          <p className="tv-token">{c.number}</p>
                          <p className="tv-patient">
                            {c.patient_name ||
                              (ml ? "പേര് ലഭ്യമല്ല" : "Name not available")}
                          </p>
                          <p className="tv-room">
                            {ml ? "ദയവായി വരിക" : "Please proceed to"}
                            <br />
                            {c.station}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="tv-token">—</p>
                    )}
                  </section>
                  {[
                    {
                      title: ml ? "അടുത്ത 5 ടോക്കണുകൾ" : "Next 5",
                      rows: q.waiting.slice(0, 5),
                      remaining: false,
                    },
                    {
                      title: ml
                        ? "ബാക്കി കാത്തിരിക്കുന്നവർ"
                        : "Remaining Waiting",
                      rows: q.waiting.slice(
                        5 + waitingPage * 5,
                        10 + waitingPage * 5,
                      ),
                      remaining: true,
                    },
                  ].map((group) => (
                    <section className="tv-waiting" key={group.title}>
                      <div className="tv-table-heading">
                        <h3>{group.title}</h3>
                        {group.remaining && (
                          <span>
                            {waitingPage + 1} /{" "}
                            {Math.max(
                              1,
                              Math.ceil(Math.max(0, q.waiting.length - 5) / 5),
                            )}
                          </span>
                        )}
                      </div>
                      <table className="tv-table">
                        <caption className="sr-only">{group.title}</caption>
                        <thead>
                          <tr>
                            <th>{ml ? "ടോക്കൺ" : "Token"}</th>
                            <th>{ml ? "പേര്" : "Patient"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((token) => (
                            <tr key={token.id}>
                              <td>{token.number}</td>
                              <td>
                                {token.patient_name ||
                                  (ml ? "പേര് ലഭ്യമല്ല" : "Name not available")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {!group.rows.length && (
                        <p className="tv-empty">
                          {ml
                            ? "കാത്തിരിക്കുന്നവർ ഇല്ല"
                            : "No patients waiting"}
                        </p>
                      )}
                    </section>
                  ))}
                  {!!q.missed?.length && (
                    <aside className="tv-missed">
                      <strong>
                        {ml
                          ? "റിസപ്ഷനുമായി ബന്ധപ്പെടുക"
                          : "Missed your turn? Please contact reception"}
                      </strong>
                      <span>
                        {q.missed
                          .slice(
                            (Math.floor(clock.getTime() / 10000) %
                              Math.ceil(q.missed.length / 5)) *
                              5,
                            (Math.floor(clock.getTime() / 10000) %
                              Math.ceil(q.missed.length / 5)) *
                              5 +
                              5,
                          )
                          .map((token) => token.number)
                          .join(" · ")}
                      </span>
                    </aside>
                  )}
                </div>
              </section>
            ))}
        </div>
      )}
      {data && !isError && !data.queues.length && (
        <p className="text-center text-2xl py-16">
          {ml ? "ഇന്നത്തേക്ക് ക്യൂകൾ ഇല്ല" : "No patients waiting"}
        </p>
      )}
    </main>
  );
}
